<template>
  <AeModal v-model="show" title="Debug Panel" size="lg">
    <div class="debug-panel">

      <!-- Auth / Storage -->
      <AeCard class="debug-section">
        <template #header><span class="section-title">Auth & Storage - test</span></template>
        <div class="debug-grid">
          <div class="debug-row">
            <span class="label">Autenticato</span>
            <span :class="['badge', appStore.isAuthenticated ? 'ok' : 'err']">
              {{ appStore.isAuthenticated ? 'Sì' : 'No' }}
            </span>
          </div>
          <div class="debug-row">
            <span class="label">UID</span>
            <span class="value">{{ appStore.user?.uid ?? '—' }}</span>
          </div>
          <div class="debug-row">
            <span class="label">SID</span>
            <span class="value">{{ appStore.user?.superuser?.sid ?? appStore.user?.sid ?? '—' }}</span>
          </div>
          <div class="debug-row">
            <span class="label">Nome</span>
            <span class="value">{{ appStore.getUserName || '—' }}</span>
          </div>
          <div class="debug-row">
            <span class="label">Profilo attivo</span>
            <span class="value mono">{{ selectedProfile || '—' }}</span>
          </div>
          <div class="debug-row">
            <span class="label">access_token</span>
            <span :class="['badge', hasToken ? 'ok' : 'err']">{{ hasToken ? 'presente' : 'assente' }}</span>
          </div>
          <div class="debug-row">
            <span class="label">Token scade</span>
            <span :class="['value', tokenExpired ? 'expired' : '']">{{ tokenExpiresLabel }}</span>
          </div>
          <div class="debug-row">
            <span class="label">refresh_token</span>
            <span :class="['badge', hasRefresh ? 'ok' : 'err']">{{ hasRefresh ? 'presente' : 'assente' }}</span>
          </div>
          <div class="debug-row">
            <span class="label">serverConfig</span>
            <span :class="['badge', hasServerConfig ? 'ok' : 'err']">{{ hasServerConfig ? 'ok' : 'assente' }}</span>
          </div>
          <div class="debug-row">
            <span class="label">Ruoli</span>
            <span class="value">{{ appStore.user?.roles?.join(', ') || '—' }}</span>
          </div>
          <div class="debug-row full">
            <span class="label">user (raw)</span>
          </div>
          <div class="debug-row full mono-block">
            <pre>{{ appStore.user ? JSON.stringify(appStore.user, null, 2) : '—' }}</pre>
          </div>
        </div>
      </AeCard>

      <!-- DB State -->
      <AeCard class="debug-section">
        <template #header><span class="section-title">Database locale (Dexie)</span></template>
        <div class="debug-grid">
          <div class="debug-row">
            <span class="label">isReady</span>
            <span :class="['badge', dbStore.isReady ? 'ok' : 'warn']">{{ dbStore.isReady }}</span>
          </div>
          <div class="debug-row">
            <span class="label">isSynced</span>
            <span :class="['badge', dbStore.isSynced ? 'ok' : 'warn']">{{ dbStore.isSynced }}</span>
          </div>
          <div class="debug-row">
            <span class="label">isEmpty</span>
            <span :class="['badge', dbStore.isEmpty ? 'warn' : 'ok']">{{ dbStore.isEmpty }}</span>
          </div>
          <div class="debug-row">
            <span class="label">needsResync</span>
            <span :class="['badge', dbStore.needsResync ? 'warn' : 'ok']">{{ dbStore.needsResync }}</span>
          </div>
          <div class="debug-row">
            <span class="label">syncInProgress</span>
            <span class="badge" :class="dbStore.syncInProgress ? 'warn' : 'ok'">{{ dbStore.syncInProgress }}</span>
          </div>
          <div class="debug-row">
            <span class="label">syncCheckInProgress</span>
            <span class="badge" :class="dbStore.syncCheckInProgress ? 'warn' : 'ok'">{{ dbStore.syncCheckInProgress }}</span>
          </div>
          <div class="debug-row">
            <span class="label">lastSyncTime</span>
            <span class="value mono">{{ dbStore.lastSyncTime ? new Date(dbStore.lastSyncTime).toLocaleString() : '—' }}</span>
          </div>
          <template v-if="dbStore.errors?.length">
            <div class="debug-row full">
              <span class="label">Errori DB</span>
            </div>
            <div v-for="(e, i) in dbStore.errors" :key="i" class="debug-row full">
              <span class="value err-text">{{ e }}</span>
            </div>
          </template>
          <div v-else class="debug-row">
            <span class="label">Errori DB</span>
            <span class="badge ok">nessuno</span>
          </div>
        </div>
      </AeCard>

      <!-- Sync / SSE -->
      <AeCard class="debug-section">
        <template #header><span class="section-title">Sync & SSE</span></template>
        <div class="debug-grid">
          <div class="debug-row">
            <span class="label">SSE connesso</span>
            <span :class="['badge', dbStore.streamConnected ? 'ok' : 'err']">{{ dbStore.streamConnected }}</span>
          </div>
          <div class="debug-row">
            <span class="label">Errori SSE (backoff)</span>
            <span :class="['badge', dbStore.streamErrorCount === 0 ? 'ok' : 'warn']">{{ dbStore.streamErrorCount }}</span>
          </div>
          <div class="debug-row">
            <span class="label">Events ricevuti</span>
            <span class="value">{{ dbStore.streamEventCount }}</span>
          </div>
          <div class="debug-row">
            <span class="label">isOffline</span>
            <span :class="['badge', appStore.isOffline ? 'warn' : 'ok']">{{ appStore.isOffline }}</span>
          </div>
          <div class="debug-row">
            <span class="label">syncError</span>
            <span :class="['value', dbStore.syncError ? 'err-text' : '']">{{ dbStore.syncError ? JSON.stringify(dbStore.syncError) : '—' }}</span>
          </div>
          <div class="debug-row full">
            <span class="label">farmSyncMap</span>
          </div>
          <div class="debug-row full mono-block">
            <pre>{{ farmSyncMapFormatted }}</pre>
          </div>
        </div>
      </AeCard>

    </div>

    <template #footer>
      <div class="debug-actions">
        <AeButton variant="danger" size="sm" @click="doForceResync" :disabled="resyncing">
          {{ resyncing ? 'Resync...' : 'Forza Resync DB' }}
        </AeButton>
        <AeButton variant="secondary" size="sm" @click="doReconnectStream">
          Riconnetti SSE
        </AeButton>
        <AeButton variant="secondary" size="sm" @click="goSafeArea">
          Safe Area Debug
        </AeButton>
        <AeButton variant="ghost" size="sm" @click="show = false">
          Chiudi
        </AeButton>
      </div>
    </template>
  </AeModal>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import { AeModal, AeCard, AeButton } from '@aevue/components'
import { useDbStore } from '../stores/dbStore'
import { useAppStore } from '../stores/app'

const show = defineModel({ type: Boolean, default: false })

const dbStore = useDbStore()
const appStore = useAppStore()

const resyncing = ref(false)

const selectedProfile = computed(() => localStorage.getItem('selected_profile'))
const hasToken = computed(() => !!localStorage.getItem('access_token'))
const hasRefresh = computed(() => !!localStorage.getItem('refresh_token'))
const hasServerConfig = computed(() => !!localStorage.getItem('poderi2_server_config'))

const tokenExpired = computed(() => {
  const exp = localStorage.getItem('expires_at')
  if (!exp) return true
  return Date.now() > parseInt(exp)
})

const tokenExpiresLabel = computed(() => {
  const exp = localStorage.getItem('expires_at')
  if (!exp) return '—'
  const d = new Date(parseInt(exp))
  const diffMin = Math.round((d - Date.now()) / 60000)
  if (diffMin < 0) return `scaduto ${Math.abs(diffMin)} min fa`
  return `tra ${diffMin} min (${d.toLocaleTimeString()})`
})

const farmSyncMapFormatted = computed(() => {
  const map = dbStore.farmSyncMap
  if (!map || Object.keys(map).length === 0) return '(vuoto)'
  return Object.entries(map)
    .map(([k, v]) => `${k.slice(0, 8)}…: ${v}`)
    .join('\n')
})

async function doForceResync() {
  resyncing.value = true
  try {
    await appStore.forceFullResync()
  } finally {
    resyncing.value = false
  }
}

function doReconnectStream() {
  dbStore.streamErrorCount = 0
  dbStore.connectStream()
}
</script>

<style scoped>
.debug-panel {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.debug-section {
  font-size: 13px;
}

.section-title {
  font-weight: 600;
  font-size: 13px;
}

.debug-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4px 12px;
  padding: 4px 0;
}

.debug-row {
  display: contents;
}

.debug-row.full {
  display: block;
  grid-column: 1 / -1;
  margin-top: 4px;
}

.label {
  color: var(--text-muted, #718096);
  padding: 2px 0;
  white-space: nowrap;
}

.value {
  font-family: monospace;
  font-size: 12px;
  padding: 2px 0;
  word-break: break-all;
}

.value.expired {
  color: var(--color-danger, #e53e3e);
}

.mono {
  font-family: monospace;
  font-size: 12px;
}

.mono-block {
  grid-column: 1 / -1;
}

.mono-block pre {
  margin: 0;
  font-size: 11px;
  white-space: pre-wrap;
  word-break: break-all;
  background: var(--card-bg, rgba(0,0,0,0.04));
  border-radius: 4px;
  padding: 6px 8px;
  max-height: 120px;
  overflow-y: auto;
}

.badge {
  display: inline-block;
  padding: 1px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
  align-self: center;
}

.badge.ok {
  background: rgba(16, 185, 129, 0.15);
  color: #065f46;
}

.badge.warn {
  background: rgba(245, 158, 11, 0.15);
  color: #92400e;
}

.badge.err {
  background: rgba(239, 68, 68, 0.15);
  color: #991b1b;
}

.err-text {
  color: var(--color-danger, #e53e3e);
  font-size: 12px;
}

.debug-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}
</style>
